import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { getRouteRequirement } from '../lib/routePermissions';

interface PermissionContextValue {
  permissions: Set<string>;
  enabledModules: Set<string>;
  role: string | null;
  isSuperAdmin: boolean;
  isLoading: boolean;
  /** null while loading; null also returned by server for super_admin (frontend handles separately) */
  visiblePaths: string[] | null;
  /** e.g. "PROFESSIONAL" for hospital groups, null for independent clinics or super_admin */
  planType: string | null;
  /** e.g. "ACTIVE", "TRIAL", "EXPIRED" — null for independent clinics or super_admin */
  subscriptionStatus: string | null;
  /** Check if user has a specific permission code */
  can: (permission: string) => boolean;
  /** Check if a module is enabled for the clinic */
  moduleEnabled: (moduleCode: string) => boolean;
  /** Check if a nav path should be visible (always true for super_admin) */
  isPathVisible: (path: string) => boolean;
  /**
   * Whether the user can actually access a route (module enabled + permission held), per the shared
   * ROUTE_PERMISSIONS map. Used to hide nav items / buttons the user can't use. Routes with no
   * mapped requirement return true (governed elsewhere).
   */
  canAccessPath: (path: string) => boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: new Set(),
  enabledModules: new Set(),
  role: null,
  isSuperAdmin: false,
  isLoading: true,
  visiblePaths: null,
  planType: null,
  subscriptionStatus: null,
  can: () => false,
  moduleEnabled: () => false,
  isPathVisible: () => false,
  canAccessPath: () => false,
});

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  // Use useAuth() so this provider re-renders reactively when the user logs in or out.
  // Previously it read secureStorage directly (non-reactive), which meant the query
  // never started after a fresh login — causing empty nav and permission redirects.
  const { user, loading: authLoading } = useAuth();
  const { activeClinicId } = useTenant();

  const { data, isLoading } = useQuery({
    // Include activeClinicId so permissions (nav paths, modules) refresh when branch changes.
    // Group admin switching branches gets fresh visiblePaths for the new branch's modules.
    queryKey: ['my-permissions', user?.id, activeClinicId],
    queryFn: async () => {
      const res = await api.auth.getMyPermissions();
      return res.data?.data || res.data;
    },
    // Only fetch when auth has resolved and a user is present
    enabled: !!user && !authLoading,
    // 30 s staleTime: short enough that admin changes propagate quickly via polling,
    // long enough to avoid hammering the server on every render.
    // The WebSocket bridge in Layout.tsx provides instant invalidation in practice.
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Refetch when the user returns to the tab — catches changes made while away
    refetchOnWindowFocus: true,
    retry: false,
  });

  const value = useMemo<PermissionContextValue>(() => {
    const permSet = new Set<string>(data?.permissions ?? []);
    const moduleSet = new Set<string>(data?.enabledModules ?? []);
    const isSuperAdmin = data?.isSuperAdmin ?? false;
    const visiblePaths: string[] | null = data?.visiblePaths ?? null;
    const visibleSet = visiblePaths ? new Set(visiblePaths) : null;

    return {
      permissions: permSet,
      enabledModules: moduleSet,
      role: data?.role ?? null,
      isSuperAdmin,
      // Loading while auth is resolving OR while the permissions query is in flight
      isLoading: authLoading || (isLoading && !!user),
      visiblePaths,
      planType: data?.planType ?? null,
      subscriptionStatus: data?.subscriptionStatus ?? null,
      can: (permission: string) => permSet.has(permission),
      moduleEnabled: (moduleCode: string) => moduleSet.has(moduleCode),
      isPathVisible: (path: string) => isSuperAdmin || visibleSet?.has(path) === true,
      canAccessPath: (path: string) => {
        if (isSuperAdmin) return true;
        const req = getRouteRequirement(path);
        if (!req) return true; // no mapped requirement — governed by visiblePaths / route guards
        if (req.roles && (!data?.role || !req.roles.includes(data.role))) return false;
        if (req.module && !moduleSet.has(req.module)) return false;
        if (req.permission && !permSet.has(req.permission)) return false;
        return true;
      },
    };
  }, [data, isLoading, user, authLoading]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

